import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, TextField, Button, Typography, IconButton, Avatar, Badge, Drawer, Slide, CircularProgress, Fade } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { styled, keyframes } from '@mui/material/styles';
import apiClient from "../../request/http-common";

// Define animations
const pulseAnimation = keyframes`
  0%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
`;

const ChatButton = styled(Button)(({ theme }) => ({
  position: 'fixed',
  bottom: '80px',
  right: '20px',
  borderRadius: '50%',
  width: '60px',
  height: '60px',
  minWidth: '60px',
  boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
  zIndex: 9999,
  backgroundColor: '#e17800',
  '&:hover': {
    backgroundColor: '#c56700',
  }
}));

const ChatDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 320,
    maxWidth: '100%',
    height: 500,
    maxHeight: '80vh',
    borderRadius: '10px 10px 0 0',
    bottom: '80px',
    right: '20px',
    left: 'auto',
    position: 'fixed',
    overflow: 'hidden',
    zIndex: 9998,
  },
  zIndex: 9998,
}));

const MessageBubble = styled(Box)(({ theme, sender, status }) => ({
  backgroundColor: sender === 'visitor' 
    ? '#e17800' 
    : '#e3f2fd',
  color: sender === 'visitor' 
    ? '#ffffff' 
    : '#212529',
  borderRadius: sender === 'visitor'
    ? '18px 18px 0 18px'
    : '18px 18px 18px 0',
  boxShadow: sender === 'visitor'
    ? '0 1px 2px rgba(0,0,0,0.1)'
    : '0 1px 2px rgba(0,0,0,0.05)',
  padding: '10px 16px',
  marginBottom: '8px',
  maxWidth: '80%',
  wordBreak: 'break-word',
  alignSelf: sender === 'visitor' ? 'flex-end' : 'flex-start',
  borderLeft: sender === 'admin' ? '3px solid #bbdefb' : 'none',
  opacity: status === 'sending' ? 0.7 : 1,
  transform: status === 'sent' ? 'translateY(0)' : (status === 'sending' ? 'translateY(5px)' : 'translateY(0)'),
  transition: 'opacity 0.3s ease, transform 0.3s ease',
}));

const SystemMessage = styled(Box)(({ theme }) => ({
  backgroundColor: '#edf2f7',
  color: '#4a5568',
  borderRadius: '10px',
  padding: '6px 12px',
  margin: '12px auto',
  fontSize: '0.85rem',
  maxWidth: '90%',
  textAlign: 'center',
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  border: '1px solid #e2e8f0',
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#e17800',
  color: theme.palette.common.white,
  padding: '12px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const ChatMessages = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f9f9f9',
}));

const ChatFooter = styled(Box)(({ theme }) => ({
  padding: '10px',
  borderTop: '1px solid #eee',
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
}));

const Copyright = styled(Box)(({ theme }) => ({
  padding: '5px 10px',
  borderTop: '1px solid #eee',
  backgroundColor: '#f9f9f9',
  textAlign: 'center',
  fontSize: '11px',
  color: '#888',
}));

const MessageStatus = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  marginTop: '4px',
  fontSize: '11px',
}));

const PulseLoader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '3px',
  margin: '4px 0',
  '& > span': {
    width: '6px',
    height: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: '50%',
    display: 'inline-block',
    animation: `${pulseAnimation} 1.5s infinite ease-in-out`,
    '&:nth-of-type(2)': {
      animationDelay: '0.2s',
    },
    '&:nth-of-type(3)': {
      animationDelay: '0.4s',
    },
  }
}));

const LiveChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatSession, setChatSession] = useState({
    visitorToken: localStorage.getItem('tTradesChatToken') || null,
    chatId: null,
    hasActiveChat: false,
    isInitialized: false,
    userInfo: {
      name: '',
      email: ''
    },
    showForm: false,
    unreadCount: 0
  });
  const messagesEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.imexsbrokerage.com';

  // Initialize chat when component loads
  useEffect(() => {
    if (!chatSession.isInitialized) {
      initializeChat();
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Setup polling for messages
  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    if (chatSession.visitorToken) {
      // Set up different polling intervals based on whether chat is open
      const interval = setInterval(() => {
        if (isOpen && chatSession.hasActiveChat) {
          // When chat is open, poll more frequently
          fetchChatHistory();
        } else if (chatSession.hasActiveChat) {
          // When chat is minimized but active, check for unread messages
          checkForNewMessages();
        }
      }, isOpen ? 3000 : 10000); // 3 seconds when open, 10 seconds when closed

      pollingIntervalRef.current = interval;
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isOpen, chatSession.visitorToken, chatSession.hasActiveChat]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history when opening the chat
  useEffect(() => {
    if (isOpen && chatSession.visitorToken && chatSession.isInitialized) {
      fetchChatHistory();
    }
  }, [isOpen, chatSession.visitorToken, chatSession.isInitialized]);

  const initializeChat = async () => {
    try {
      const response = await apiClient.post('/live-chat/init', {
        visitor_token: chatSession.visitorToken,
        page_url: window.location.href,
        page_title: document.title,
        referrer: document.referrer
      });
      
      if (response.data.status === 'success') {
        const token = response.data.visitor_token;
        localStorage.setItem('tTradesChatToken', token);
        
        setChatSession(prev => ({
          ...prev,
          visitorToken: token,
          isInitialized: true
        }));
      }
    } catch (error) {
      console.error('Failed to initialize chat:', error);
    }
  };

  const fetchChatHistory = async () => {
    if (!chatSession.visitorToken) return;
    
    try {
      setLoading(true);
      const response = await apiClient.post('/live-chat/history', {
        visitor_token: chatSession.visitorToken
      });
      
      if (response.data.status === 'success') {
        if (response.data.has_active_chat) {
          setMessages(formatMessages(response.data.messages));
          setChatSession(prev => ({
            ...prev,
            chatId: response.data.chat_id,
            hasActiveChat: true,
            showForm: false,
            unreadCount: 0
          }));
        } else {
          setChatSession(prev => ({
            ...prev,
            hasActiveChat: false,
            showForm: true,
            unreadCount: 0
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkForNewMessages = async () => {
    if (!chatSession.visitorToken || !chatSession.chatId) return;
    
    try {
      const response = await apiClient.post('/live-chat/history', {
        visitor_token: chatSession.visitorToken
      });
      
      if (response.data.status === 'success' && response.data.has_active_chat) {
        setChatSession(prev => ({
          ...prev,
          unreadCount: response.data.unread_count || 0
        }));
      }
    } catch (error) {
      console.error('Failed to check for new messages:', error);
    }
  };

  const formatMessages = (messageArray) => {
    if (!messageArray) return [];
    return messageArray.map(msg => ({
      id: msg.id,
      text: msg.message,
      sender: msg.sender_type,
      time: new Date(msg.created_at),
      isSystem: msg.sender_type === 'system',
      status: 'sent'  // All server messages are considered 'sent'
    }));
  };

  const startNewChat = async () => {
    if (!message.trim() || !chatSession.visitorToken) return;
    
    const messageText = message;
    setMessage('');
    setSendingMessage(true);
    
    // Optimistically add message with 'sending' status
    const newMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'visitor',
      time: new Date(),
      isSystem: false,
      status: 'sending'
    };
    
    setMessages([newMessage]);
    
    try {
      setLoading(true);
      const response = await apiClient.post('/live-chat/start', {
        visitor_token: chatSession.visitorToken,
        message: messageText,
        name: chatSession.userInfo.name,
        email: chatSession.userInfo.email,
        page_url: window.location.href,
        page_title: document.title
      });
      
      if (response.data.status === 'success') {
        // Set message status to 'sent'
        setMessages(prev => 
          prev.map(msg => 
            msg.id === newMessage.id 
              ? { ...msg, status: 'sent' } 
              : msg
          )
        );
        
        // After a brief delay to show the sent status, update with server response
        setTimeout(() => {
          setMessages(formatMessages(response.data.messages));
          setChatSession(prev => ({
            ...prev,
            chatId: response.data.chat_id,
            hasActiveChat: true,
            showForm: false
          }));
        }, 300);
      }
    } catch (error) {
      console.error('Failed to start chat:', error);
      // Set message status to 'error'
      setMessages(prev => 
        prev.map(msg => 
          msg.id === newMessage.id 
            ? { ...msg, status: 'error' } 
            : msg
        )
      );
    } finally {
      setLoading(false);
      setSendingMessage(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !chatSession.visitorToken || !chatSession.chatId) return;
    
    const messageText = message;
    setMessage('');
    setSendingMessage(true);
    
    // Optimistically add message with 'sending' status
    const newMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'visitor',
      time: new Date(),
      isSystem: false,
      status: 'sending'
    };
    
    setMessages(prev => [...prev, newMessage]);
    
    try {
      await apiClient.post('/live-chat/send', {
        visitor_token: chatSession.visitorToken,
        chat_id: chatSession.chatId,
        message: messageText
      });
      
      // Set message status to 'sent'
      setMessages(prev => 
        prev.map(msg => 
          msg.id === newMessage.id 
            ? { ...msg, status: 'sent' } 
            : msg
        )
      );
      
      // After a brief delay to show the sent status, fetch updated messages
      setTimeout(() => {
        fetchChatHistory();
      }, 300);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Set message status to 'error'
      setMessages(prev => 
        prev.map(msg => 
          msg.id === newMessage.id 
            ? { ...msg, status: 'error' } 
            : msg
        )
      );
    } finally {
      setSendingMessage(false);
    }
  };

  const closeChat = async () => {
    if (!chatSession.chatId) return;
    
    try {
      setLoading(true);
      await apiClient.post('/live-chat/close', {
        visitor_token: chatSession.visitorToken,
        chat_id: chatSession.chatId
      });
      
      // Add system message to UI
      const systemMessage = {
        id: Date.now(),
        text: 'Chat ended by you',
        sender: 'system',
        time: new Date(),
        isSystem: true
      };
      
      setMessages(prev => [...prev, systemMessage]);
      
      // Reset chat session
      setChatSession(prev => ({
        ...prev,
        chatId: null,
        hasActiveChat: false,
        showForm: true
      }));
    } catch (error) {
      console.error('Failed to close chat:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;
    setChatSession(prev => ({
      ...prev,
      userInfo: {
        ...prev.userInfo,
        [name]: value
      }
    }));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Reset unread count when opening chat
      setChatSession(prev => ({
        ...prev,
        unreadCount: 0
      }));
      // Force refetch chat history
      fetchChatHistory();
    }
  };

  const formatTime = (time) => {
    return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (chatSession.hasActiveChat) {
        sendMessage();
      } else if (chatSession.showForm) {
        startNewChat();
      }
    }
  };

  // Render message status indicator
  const renderMessageStatus = (status, time) => {
    switch (status) {
      case 'sending':
        return (
          <PulseLoader>
            <span></span>
            <span></span>
            <span></span>
          </PulseLoader>
        );
      case 'sent':
        return (
          <MessageStatus>
            <Typography variant="caption" sx={{ mr: 0.5, opacity: 0.7 }}>
              {formatTime(time)}
            </Typography>
            <DoneIcon sx={{ fontSize: 12, opacity: 0.7 }} />
          </MessageStatus>
        );
      case 'delivered':
        return (
          <MessageStatus>
            <Typography variant="caption" sx={{ mr: 0.5, opacity: 0.7 }}>
              {formatTime(time)}
            </Typography>
            <DoneAllIcon sx={{ fontSize: 12, opacity: 0.7 }} />
          </MessageStatus>
        );
      case 'error':
        return (
          <MessageStatus>
            <Typography variant="caption" sx={{ mr: 0.5, color: '#ff5252' }}>
              Failed to send
            </Typography>
          </MessageStatus>
        );
      default:
        return (
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, opacity: 0.7 }}>
            {formatTime(time)}
          </Typography>
        );
    }
  };

  return (
    <>
      <ChatButton 
        color="inherit" 
        variant="contained" 
        onClick={toggleChat}
        aria-label="Open chat"
      >
        <Badge badgeContent={chatSession.unreadCount} color="error">
          <ChatIcon />
        </Badge>
      </ChatButton>

      <ChatDrawer
        anchor="right"
        open={isOpen}
        onClose={toggleChat}
        variant="persistent"
      >
        <Paper elevation={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <ChatHeader>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar 
                src="https://res.cloudinary.com/dvoynhghq/image/upload/v1713721717/mainttradelogo3d_tlfyti.png" 
                alt="IMEXS BROKERAGE" 
                sx={{ width: 32, height: 32, mr: 1 }} 
              />
              <Typography variant="h6">IMEXS BROKERAGE Support</Typography>
            </Box>
            <IconButton 
              size="small" 
              color="inherit" 
              onClick={toggleChat}
              aria-label="Close chat"
            >
              <CloseIcon />
            </IconButton>
          </ChatHeader>

          <ChatMessages>
            {loading && messages.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress size={24} sx={{ mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Loading messages...
                </Typography>
              </Box>
            ) : !chatSession.hasActiveChat && messages.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Start a conversation with our team.
                </Typography>
              </Box>
            ) : (
              messages.map((msg) => (
                msg.isSystem ? (
                  <Fade in={true} key={msg.id} timeout={500}>
                    <SystemMessage>{msg.text}</SystemMessage>
                  </Fade>
                ) : (
                  <Fade in={true} key={msg.id} timeout={300}>
                    <Box sx={{ mb: 2 }}>
                      {msg.sender !== 'visitor' && (
                        <Typography 
                          variant="caption" 
                          display="block" 
                          sx={{ textAlign: 'left', mb: 0.5 }}
                        >
                          {msg.sender}
                        </Typography>
                      )}
                      <MessageBubble 
                        sender={msg.sender}
                        status={msg.status}
                        sx={{ mb: 0.5 }}
                      >
                        <Typography variant="body2">{msg.text}</Typography>
                      </MessageBubble>
                      {msg.sender === 'visitor' && renderMessageStatus(msg.status, msg.time)}
                    </Box>
                  </Fade>
                )
              ))
            )}
            <div ref={messagesEndRef} />
          </ChatMessages>

          <ChatFooter>
            {chatSession.showForm ? (
              <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Your Name (optional)"
                  name="name"
                  value={chatSession.userInfo.name}
                  onChange={handleUserInfoChange}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Your Email (optional)"
                  name="email"
                  value={chatSession.userInfo.email}
                  onChange={handleUserInfoChange}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  placeholder="Type your message here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  sx={{ flex: 1 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button 
                    variant="contained" 
                    style={{ backgroundColor: '#e17800' }}
                    endIcon={sendingMessage ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    onClick={startNewChat}
                    disabled={!message.trim() || loading || sendingMessage}
                  >
                    Start Chat
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  placeholder="Type your message here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  sx={{ flex: 1 }}
                />
                <IconButton 
                  style={{ color: '#e17800' }}
                  onClick={sendMessage}
                  disabled={!message.trim() || loading || sendingMessage}
                  sx={{ ml: 1 }}
                  aria-label="Send message"
                >
                  {sendingMessage ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                </IconButton>
              </>
            )}
          </ChatFooter>

          {chatSession.hasActiveChat && (
            <Box sx={{ p: 1, borderTop: '1px solid #eee', textAlign: 'center' }}>
              <Button 
                size="small" 
                color="error" 
                onClick={closeChat}
                disabled={loading}
              >
                End Chat
              </Button>
            </Box>
          )}
          
          <Copyright>
            © {new Date().getFullYear()} IMEXS BROKERAGE. All rights reserved.
          </Copyright>
        </Paper>
      </ChatDrawer>
    </>
  );
};

export default LiveChatWidget; 